import React, { useEffect, useState } from 'react';
import { CodingConstants } from '@constants';
import {
    selectEligibleForNDByLoanNumber,
    selectSortedWidgetsData,
    selectWidgets,
    selectLoanStatusByLoanNumber
} from '@store/selectors/widgets.selectors';
import { Documents, Offer, Statements, WidgetContainerProps, WidgetTemplate } from '@types';
import { filterCards } from '@utils/filterWidgets';
import { useSelector } from 'react-redux';
import DashboardContainer from './DashboardContainer';
import { EscrowContainer } from './EscrowContainer';
import { NewLoanInProgressContainer } from './NewLoanInProgressContainer';
import { NewLoanInProgressContainerV2 } from './NewLoanInProgressContainerV2';
import { dispatch } from '@store';
import { fetchStatementAsync } from '@store/actions/statements.action';
import { isEmpty } from 'lodash';
import { UIConstants } from '@constants/UIConstants';
import {
    selectEscrowAnalysisDocument,
    selectMonthlyStatement,
    selectstatements,
    selectTaxDocument
} from '@store/selectors/statement.selector';
import AlertModal from '@components/AlertModal/AlertModal';
import { WidgetsContext } from '@context';
import { ModalAlert } from '@components/ModalAlert/ModalAlert';
import Loader from '@components/Loader/Loader';
import { CashOutContainer } from './CashOutContainer';
import { RefiContainer } from './RefiContainer';
import NaturalDisasterAlertComponent from '@components/NaturalDisaster/NaturalDisasterAlertComponent';
import NaturalDisasterACP from './NaturalDisaster';
import { PurchaseContainer } from '@containers/PurchaseContainer';
import { getFullAppFeatureFlags } from '@store/reducers/config.reducer';
import { setOfferTypeSelected } from '@store/actions/widgets.actions';
import { getLipV2FeatureFlag } from '@store/reducers/config.reducer';
import { setAdditionalProperties } from 'fmc-digital-common';
import { useLocation } from 'react-router-dom';
import MessageCenterContainer from './MessageCenterContainer';
import { config } from '../config';
import { setDocumentUrl } from '@store/actions/document.action';

const WidgetsContainer: React.FC<WidgetContainerProps> = ({
    loanNumber,
    content,
    getOption,
    updateLoanType,
    propertyState,
    userName,
    displayPage
}) => {
    const {
        escrow,
        servicingLoanType,
        inProgressLoanType,
        authCashOut,
        monthlyStmt,
        taxDoc,
        authRefi,
        escrowDoc,
        naturalDisaster,
        naturalDisasterAssistance,
        purchaseWidgetId,
        inProgressLoanTypeV2
    } = CodingConstants;
    const { errorTitle, errorDetails, ok, closeIconRed } = UIConstants;

    const widgets: WidgetTemplate[] = useSelector(selectSortedWidgetsData);
    const widgetApiResponse: any = useSelector(selectWidgets);
    const statements: Statements = useSelector(selectstatements);
    const monthlyStatement: Documents[] = useSelector(selectMonthlyStatement);
    const taxDocument = useSelector(selectTaxDocument);
    const escrowAnalysisDocument = useSelector(selectEscrowAnalysisDocument);
    const naturalDisasterRes = useSelector(selectEligibleForNDByLoanNumber(loanNumber));
    const loanStatus: number = useSelector(selectLoanStatusByLoanNumber(loanNumber));

    const nlpWidgetList = filterCards(loanNumber, widgetApiResponse);
    const [disasterAlert, setDisasterAlert] = useState({ showAlert: !!naturalDisasterRes, borrowerImpacted: '' });

    const [docType, setDocType] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [offerResponse, setOfferResponse] = useState<Offer>();
    const flags = useSelector(getFullAppFeatureFlags);
    const lipV2flags = useSelector(getLipV2FeatureFlag);
    const getOfferReponse = (response: Offer) => {
        setOfferResponse(response);
        if (response && response?.product_Campaign) dispatch(setOfferTypeSelected(response.product_Campaign));
    };

    const docRedirect = () => {
        window.open(config.env('redirectUri') + '#/document');
    };
    const handleDocument = (type: string, loanNumber: string) => {
        if (isEmpty(statements) || statements?.errorDetails) {
            setShowLoader(true);
            setDocType(type);
            // dispatch(fetchStatementAsync.request(loanNumber));
        } else if (!monthlyStatement && !taxDocument && !escrowAnalysisDocument) {
            setShowAlert(true);
        } else {
            if (type === monthlyStmt && monthlyStatement) {
                const docUrl = `${monthlyStatement[0]?.url}`;
                dispatch(setDocumentUrl(docUrl));
                docRedirect();
            } else if (type === taxDoc && taxDocument) {
                const docUrl = `${taxDocument[0]?.url}`;
                dispatch(setDocumentUrl(docUrl));
                docRedirect();
            } else if (type === escrowDoc && escrowAnalysisDocument) {
                const docUrl = `${escrowAnalysisDocument[0]?.url}`;
                dispatch(setDocumentUrl(docUrl));
                docRedirect();
            } else {
                setShowAlert(true);
            }
        }
    };
    useEffect(() => {
        if (docType) {
            if (!isEmpty(statements)) {
                setShowLoader(false);
                if (docType === monthlyStmt) {
                    if (monthlyStatement) {
                        const docUrl = `${monthlyStatement[0]?.url}`;
                        dispatch(setDocumentUrl(docUrl));
                        docRedirect();
                    } else {
                        setShowAlert(true);
                    }
                } else if (docType === taxDoc) {
                    if (taxDocument) {
                        const docUrl = `${taxDocument[0]?.url}`;
                        dispatch(setDocumentUrl(docUrl));
                        docRedirect();
                    } else {
                        setShowAlert(true);
                    }
                } else if (docType === escrowDoc) {
                    if (escrowAnalysisDocument) {
                        const docUrl = `${escrowAnalysisDocument[0]?.url}`;
                        dispatch(setDocumentUrl(docUrl));
                        docRedirect();
                    } else {
                        setShowAlert(true);
                    }
                }
            } else if (statements && statements?.errorDetails) {
                setShowAlert(true);
            }
        }
    }, [statements, monthlyStatement, docType, monthlyStmt, taxDoc, escrowDoc, taxDocument, escrowAnalysisDocument]);
    const handleAlertClose = () => {
        setShowAlert(false);
    };

    useEffect(() => {
        if (content === servicingLoanType) {
            setDisasterAlert({ ...disasterAlert, showAlert: !!naturalDisasterRes });
        }
    }, [naturalDisasterRes]);

    const handleDisasterAlert = (props: any) => {
        setDisasterAlert(props);
        updateLoanType(naturalDisaster);
    };

    const renderDashboard = () => {
        return (
            <>
                <Loader showLoader={showLoader} />
                {disasterAlert.showAlert && (
                    <AlertModal maxWidth="auto" title="Natural Disaster Alert" showCloseIcon={false} data-testid="natural_disaster_alert">
                        <div style={{ textAlign: 'center' }}>
                            <NaturalDisasterAlertComponent setDisasterAlert={handleDisasterAlert} loanNumber={loanNumber} />
                        </div>
                    </AlertModal>
                )}
                {showAlert && (
                    <AlertModal title={errorTitle} handleClose={handleAlertClose}>
                        <ModalAlert details={errorDetails} image={closeIconRed} buttonText={ok} handleClose={handleAlertClose} />
                    </AlertModal>
                )}
                {content === servicingLoanType && (
                    <DashboardContainer loanNumber={loanNumber} cards={widgets} updateLoanType={updateLoanType} getOption={getOption} />
                )}

                {content === inProgressLoanTypeV2 && lipV2flags && (
                    <NewLoanInProgressContainerV2
                        loanNumber={loanNumber}
                        nlpWidgetList={nlpWidgetList}
                        loanStatus={loanStatus}
                        getOption={getOption}
                    />
                )}
                {content === inProgressLoanType && !lipV2flags && (
                    <NewLoanInProgressContainer loanNumber={loanNumber} nlpWidgetList={nlpWidgetList} />
                )}
                {content === escrow && <EscrowContainer loanNumber={loanNumber} propertyState={propertyState} />}
                {content === authCashOut && (
                    <CashOutContainer loanNumber={loanNumber} updateLoanType={updateLoanType} offerDetails={offerResponse} />
                )}
                {content === purchaseWidgetId && (
                    <PurchaseContainer loanNumber={loanNumber} updateLoanType={updateLoanType} offerDetails={offerResponse} />
                )}
                {content === authRefi && (
                    <RefiContainer loanNumber={loanNumber} updateLoanType={updateLoanType} offerDetails={offerResponse} flags={flags} />
                )}
                {content === naturalDisaster && (
                    <NaturalDisasterACP
                        borrowerImpacted={disasterAlert.borrowerImpacted}
                        loanNumber={loanNumber}
                        updateLoanType={updateLoanType}
                        getOption={getOption}
                    />
                )}
                {content === naturalDisasterAssistance && (
                    <NaturalDisasterACP loanNumber={loanNumber} updateLoanType={updateLoanType} getOption={getOption} />
                )}
            </>
        );
    };
    const renderComponent = () => {
        switch (displayPage) {
            case 'messageCenter':
                return (
                    <div id="messageCenterContainer">
                        <MessageCenterContainer />
                    </div>
                );
            case 'DashBoard':
                return renderDashboard();
        }
    };

    return (
        <WidgetsContext.Provider value={{ handleDocument, getOfferReponse, loanNumber, userName }}>
            {renderComponent()}
        </WidgetsContext.Provider>
    );
};

export default React.memo(WidgetsContainer);
